import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { FormAwareMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareMultiSelectInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { TrustTrustees } from '@/modules/content/tooltipContent/TrustTrustees';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { TrusteeDropdown } from '@/modules/entities/inputs/TrusteeDropdown/TrusteeDropdown';
import {
  emptyTrusteeValue,
  Fields,
  SubformField,
  TRUST_DETAILS_SUBFORM_NAMESPACE,
  TrustDetailsSubformType,
  TrustPersonDropdownFieldProps,
  TrustPersonMultiSelectFieldProps,
  TrustPersonRepeaterProps,
} from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';
import { TRUSTEE_DUTY_KIND_ITEMS } from '@/modules/trustees/trustees.constants';
import { AiSuggestionKind } from '@/types/schema';

const TrusteeDropdownField = ({
  index,
  hideLabel,
  trustPersonOptions,
  householdId,
  labelIconEnd,
  splitScreen,
  additionalItemRendererProps,
  suggestionKindOverride,
}: TrustPersonDropdownFieldProps) => {
  return (
    <TrusteeDropdown
      fieldName={
        `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees.${index}.trusteeId` as const satisfies SubformField
      }
      label={'Trustees'}
      hideLabel={hideLabel}
      options={trustPersonOptions}
      contextualHelp={<TrustTrustees />}
      householdId={householdId}
      labelIconEnd={labelIconEnd}
      suggestionKind={suggestionKindOverride ?? AiSuggestionKind.Trustee}
      blockNavigationAfterCreate={splitScreen}
      additionalItemRendererProps={additionalItemRendererProps}
    />
  );
};

const TrusteeDutiesField = ({
  index,
  hideLabel,
}: TrustPersonMultiSelectFieldProps) => {
  const { control } = useFormContext<Fields>();

  return (
    <FormAwareMultiSelectInput<Fields>
      fieldName={
        `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees.${index}.duties` as const satisfies SubformField
      }
      label={'Duties'}
      hideLabel={hideLabel}
      control={control}
      options={TRUSTEE_DUTY_KIND_ITEMS}
    />
  );
};

export const TrusteeInputRepeater = ({
  trusteesWithoutDuties,
  trustPersonOptions,
  householdId,
  control,
  splitScreen,
  shouldGroupDuplicates,
  suggestionKindOverride,
}: TrustPersonRepeaterProps) => {
  const { duplicatesRefs } = useEditEntitySplitScreenModalContext();

  type RepeatedFormType = TrustDetailsSubformType['trustees'][0];

  const name =
    `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees` as const satisfies SubformField;

  return (
    <FormAwareInputRepeater<RepeatedFormType>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- linter refactor
      control={control as any}
      name={name}
      emptyValue={emptyTrusteeValue}
      shouldInit
      addAnotherItemText="Add additional trustee"
      render={(i: number, _props, _id, additionalItemRendererProps) => {
        const hideLabel =
          additionalItemRendererProps?.hideLabelOverride ?? i > 0;
        return (
          <FormLayoutRow>
            <FormLayoutItem width={trusteesWithoutDuties ? 12 : 8}>
              <TrusteeDropdownField
                index={i}
                hideLabel={hideLabel}
                trustPersonOptions={trustPersonOptions}
                householdId={householdId}
                splitScreen={splitScreen}
                additionalItemRendererProps={additionalItemRendererProps}
                suggestionKindOverride={suggestionKindOverride}
              />
            </FormLayoutItem>
            {!trusteesWithoutDuties && (
              <FormLayoutItem width={4} sx={{ alignSelf: 'end' }}>
                <TrusteeDutiesField index={i} hideLabel={hideLabel} />
              </FormLayoutItem>
            )}
          </FormLayoutRow>
        );
      }}
      idField="trusteeId"
      shouldGroupDuplicates={shouldGroupDuplicates}
      duplicatesRef={duplicatesRefs[name]}
    />
  );
};
